// Payments Module Types

// === Enums ===

export enum PaymentMethod {
  CASH = 'CASH',
  TRANSFER = 'TRANSFER',
  CARD = 'CARD',
  CHECK = 'CHECK',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  VERIFYING = 'VERIFYING',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

export enum ExpenseCategory {
  MAINTENANCE = 'MAINTENANCE',
  UTILITIES = 'UTILITIES',
  SALARIES = 'SALARIES',
  CLEANING = 'CLEANING',
  SECURITY = 'SECURITY',
  INSURANCE = 'INSURANCE',
  SERVICES = 'SERVICES',
  PAYROLL = 'PAYROLL',
  SUPPLIES = 'SUPPLIES',
  REFUND = 'REFUND',
  OTHER = 'OTHER',
}

// === Payment Allocation ===

export interface PaymentAllocation {
  id: string;
  chargeId: string;
  amount: number;
  chargeConcept?: string;
  chargePeriod?: string | null;
  createdAt: string;
}

// === Main Payment Interface ===

/** Cuenta efectiva que el residente vio al subir el comprobante (no cambia después). */
export interface PaymentBankSnapshot {
  bankName: string | null;
  accountHolder: string | null;
  accountNumber: string | null;
  clabe: string | null;
  source: 'SECTION' | 'TENANT' | 'NONE';
}

export interface Payment {
  id: string;
  memberId: string;
  /** Unidad pagada (1 comprobante = 1 unidad); null en pagos históricos mixtos. */
  unitId?: string | null;
  bankSnapshot?: PaymentBankSnapshot | null;
  amount: number;
  method: PaymentMethod | null;
  status: PaymentStatus;
  proofUrl: string | null;
  transactionReference: string | null;
  notes: string | null;
  paidAt: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  rejectionReason: string | null;
  invoiceUuid: string | null;
  xmlUrl: string | null;
  pdfUrl: string | null;
  invoicedAt: string | null;
  allocations: PaymentAllocation[];
  createdAt: string;
  updatedAt: string;
  memberName?: string;
  unitNumber?: string;
  allocatedAmount: number;
  unallocatedAmount: number;
}

// === DTOs ===

export interface AllocationInput {
  chargeId: string;
  amount: number;
}

export interface UploadPaymentProofDTO {
  amount: number;
  method: PaymentMethod;
  proofUrl: string;
  transactionReference?: string;
  notes?: string;
  paidAt?: string;
  allocations: AllocationInput[];
}

export interface CreateManualPaymentDTO {
  memberId: string;
  amount: number;
  method: PaymentMethod;
  transactionReference?: string;
  notes?: string;
  paidAt?: string;
  proofUrl?: string;
  allocations: AllocationInput[];
}

export interface UpdateManualPaymentDTO {
  memberId?: string;
  amount?: number;
  method?: PaymentMethod;
  transactionReference?: string | null;
  notes?: string | null;
  paidAt?: string;
  proofUrl?: string | null;
  allocations?: AllocationInput[];
}

export interface VerifyPaymentDTO {
  approved: boolean;
  rejectionReason?: string;
  notes?: string;
  adjustedAllocations?: AllocationInput[];
  invoiceUuid?: string;
  xmlUrl?: string;
  pdfUrl?: string;
}

// === Summaries ===

export interface PaymentSummary {
  totalVerifying: number;
  totalVerified: number;
  totalRejected: number;
  amountVerified: number;
  amountPending: number;
}

// === Query Params ===

export interface PaymentQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: PaymentStatus;
  method?: PaymentMethod;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  /** Solo el historial de esa unidad (residente con varias propiedades). */
  unitId?: string;
}

// === Expense (for admin) ===

export interface Expense {
  id: string;
  title: string;
  amount: number;
  category: ExpenseCategory;
  date: string;
  provider?: string;
  status: 'PAID' | 'PENDING';
  invoiceUrl?: string;
  invoiceFileId?: string;
  /** Configurable category id (replaces the fixed `category` enum going forward). */
  categoryId?: string | null;
  /** Resolved name of the configurable category, if `categoryId` is set. */
  categoryName?: string | null;
  categoryColor?: string | null;
  /** Sección del gasto. `null` = gasto general del residencial (solo ADMIN). */
  sectionId?: string | null;
}

// === Category (configurable, replaces the fixed ExpenseCategory enum) ===

export type CategoryKind = 'EXPENSE' | 'INCOME';

export interface Category {
  id: string;
  name: string;
  kind: CategoryKind;
  color: string | null;
  icon: string | null;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryDTO {
  name: string;
  kind?: CategoryKind;
  color?: string;
  icon?: string;
}

export interface UpdateCategoryDTO extends Partial<CreateCategoryDTO> {
  isActive?: boolean;
}

// === Finance Dashboard (salud financiera del módulo Finanzas) ===

export interface FinanceHealth {
  income: number;
  expenses: number;
  net: number;
  incomeDeltaPct: number | null;
  expenseDeltaPct: number | null;
}

export interface FinanceDailyPoint {
  date: string;
  income: number;
  expense: number;
}

export interface FinanceMovement {
  id: string;
  type: 'income' | 'expense';
  concept: string;
  amount: number;
  date: string;
  tag: string | null;
  status: string;
}

export interface FinanceCategoryTotal {
  categoryId: string | null;
  categoryName: string;
  color: string | null;
  total: number;
  count: number;
}

export interface FinanceDashboardResponse {
  month: string;
  health: FinanceHealth;
  daily: FinanceDailyPoint[];
  movements: FinanceMovement[];
  byCategory: FinanceCategoryTotal[];
}
